// Client batcher: every card asking for its data within the same tick shares
// one getTourCards request. Price-only and calendar reads batch separately.
import { useQuery, useQueries } from "@tanstack/react-query";
import { getTourCards, fillTourCard } from "./tour-cards.functions";
type TourCardEntry = Awaited<ReturnType<typeof getTourCards>>[string];
type Mode = "price" | "calendar";

const pending: Record<Mode, Map<string, Array<(v: TourCardEntry | null) => void>>> = { price: new Map(), calendar: new Map() };
const timers: Record<Mode, ReturnType<typeof setTimeout> | null> = { price: null, calendar: null };

function flush(mode: Mode) {
  const batch = pending[mode]; pending[mode] = new Map(); timers[mode] = null;
  getTourCards({ data: { ids: [...batch.keys()], calendar: mode === "calendar" } })
    .then(res => batch.forEach((cbs, id) => cbs.forEach(cb => cb(res[id] ?? null))))
    .catch(() => batch.forEach(cbs => cbs.forEach(cb => cb(null))));
}

const norm = (id: string) => id.replace(/^bkn-/, "");

export function loadTourCard(rawId: string, mode: Mode = "calendar"): Promise<TourCardEntry | null> {
  const id = norm(rawId);
  if (!/^\d{1,12}$/.test(id)) return Promise.resolve(null);
  return new Promise(resolve => {
    const list = pending[mode].get(id) ?? []; list.push(resolve); pending[mode].set(id, list);
    if (!timers[mode]) timers[mode] = setTimeout(() => flush(mode), 15);
  });
}

/** Price for a card; a tour missing from the cache is quoted once in the background. */
async function loadPrice(rawId: string): Promise<TourCardEntry["price"]> {
  const entry = await loadTourCard(rawId, "price");
  if (entry) return entry.price;
  const id = norm(rawId);
  if (!/^\d{1,12}$/.test(id)) return null;
  return (await fillTourCard({ data: { id } }).catch(() => ({ price: null }))).price;
}

const opts = (id: string) => ({ queryKey: ["tour-card", norm(id)], queryFn: () => loadTourCard(id, "calendar"), staleTime: 60_000, gcTime: 30 * 60_000, retry: 0 });
export const useTourCardList = (ids: string[]) => useQueries({ queries: ids.map(opts) });
export const useTourCardPrice = (id: string, initialData?: TourCardEntry["price"]) =>
  useQuery({ initialData, queryKey: ["tour-card-price", norm(id)], queryFn: () => loadPrice(id), staleTime: 60_000, gcTime: 30 * 60_000, retry: 1 });
